/**
 * SanctaCheckoutPanel — the in-chat commerce surface for Slice 2.
 *
 * Renders as an inline dialog inside the Sancta chat scroll. Manages the
 * full checkout flow end-to-end so the shopper never leaves the chat:
 *
 *   1. Cart preview (subtotal, VIP + bundle discount, GST, total).
 *   2. Shipping address collection (pre-populated with the buyer's saved
 *      default address if they have one).
 *   3. OTP unlock (only prompted when the current session is not yet
 *      unlocked). Delivered via /api/sancta/commerce/otp/start and
 *      verified via /api/sancta/commerce/otp/verify.
 *   4. Place order. Delegates to /api/sancta/commerce/checkout/place-order
 *      which internally hits the marketplace + Razorpay flow.
 *   5. Confirmation card with the order number and a "modify" hint —
 *      buyer can edit quantities / address until the item ships.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, MapPin, Loader2, ShoppingBag, CheckCircle2,
  KeyRound, ArrowRight, X, Package, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/contexts/CurrencyContext";

type Step = "preview" | "address" | "otp" | "placing" | "done";

interface CartTotals {
  items: any[];
  subtotal: number;
  vip_discount: number;
  vip_pct?: number | null;
  vip_tier?: string | null;
  bundle_discount: number;
  bundle_pct?: number | null;
  bundle_group_count?: number | null;
  shipping: number;
  tax: number;
  total: number;
  count: number;
  default_address?: any;
  otp_unlocked?: boolean;
  ttl_minutes?: number;
}

interface Address {
  name: string;
  phone: string;
  email: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

const EMPTY_ADDRESS: Address = {
  name: "", phone: "", email: "", line1: "", line2: "",
  city: "", state: "", postal_code: "", country: "IN",
};

export const SanctaCheckoutPanel: React.FC<{
  onClose: () => void;
  variant?: "widget" | "page";
}> = ({ onClose, variant = "widget" }) => {
  const { formatPrice } = useCurrency();
  const [step, setStep] = React.useState<Step>("preview");
  const [totals, setTotals] = React.useState<CartTotals | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [address, setAddress] = React.useState<Address>(EMPTY_ADDRESS);
  const [otpDispatched, setOtpDispatched] = React.useState<{ target: string; provider: string } | null>(null);
  const [otpCode, setOtpCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [orderResult, setOrderResult] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);

  const dark = variant === "page";
  const cardCls = dark ? "bg-slate-900/80 border border-white/10" : "bg-white border border-neutral-200";
  const subtleTextCls = dark ? "text-slate-400" : "text-neutral-500";
  const inputCls = `w-full px-3 py-1.5 rounded-lg text-sm ${dark ? "bg-white/10 border border-white/10 text-white placeholder:text-slate-400 focus:border-fuchsia-400" : "bg-neutral-50 border border-neutral-200 focus:border-orange-400"} focus:outline-none`;

  // 1. Load preview + saved address on mount.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/sancta/commerce/checkout/preview");
        if (!alive) return;
        setTotals(r.data);
        const saved = r.data?.default_address;
        if (saved) setAddress({ ...EMPTY_ADDRESS, ...saved });
      } catch (e: any) {
        setError(e?.response?.data?.detail || "Could not load your cart.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const patch = (k: keyof Address, v: string) => setAddress(a => ({ ...a, [k]: v }));

  function nextFromPreview() {
    if (!totals || !totals.count) {
      toast.error("Your cart is empty.");
      return;
    }
    setStep("address");
  }

  function isAddressValid(a: Address): boolean {
    return !!(a.name && a.phone && a.email && a.line1 && a.city && a.state && a.postal_code && a.country);
  }

  async function submitAddress() {
    if (!isAddressValid(address)) {
      toast.error("Please fill every required field.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/sancta/commerce/address", address);
      // If OTP is already unlocked we can go straight to placing the order.
      if (totals?.otp_unlocked) {
        await placeOrder();
      } else {
        await dispatchOtp();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not save your address.");
    } finally {
      setBusy(false);
    }
  }

  async function dispatchOtp() {
    setBusy(true);
    try {
      const r = await api.post("/sancta/commerce/otp/start", {});
      setOtpDispatched({ target: r.data?.target_masked || "your inbox", provider: r.data?.provider });
      setStep("otp");
      toast.success(`OTP sent to ${r.data?.target_masked || "your inbox"}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not dispatch OTP.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp() {
    if (!otpCode.trim() || otpCode.trim().length < 4) {
      toast.error("Enter the 6-digit code you received.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sancta/commerce/otp/verify", {
        code: otpCode.trim(),
        email: address.email,
        phone: address.phone,
      });
      await placeOrder();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function placeOrder() {
    setStep("placing");
    setBusy(true);
    try {
      const r = await api.post("/sancta/commerce/checkout/place-order", {
        shipping_address: address,
        save_address_as_default: true,
        note: "Placed via Sancta chat",
      });
      setOrderResult(r.data);
      setStep("done");
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "otp_required") {
        toast.warning("OTP expired — sending a fresh code.");
        setStep("address");
        await dispatchOtp();
      } else {
        toast.error(typeof detail === "string" ? detail : "Order placement failed.");
        setStep("address");
      }
    } finally {
      setBusy(false);
    }
  }

  const money = (n: number | null | undefined) =>
    formatPrice(Number(n || 0));

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.22 }}
      data-testid="sancta-checkout-panel"
      className={`rounded-2xl p-3 space-y-3 ${cardCls}`}
    >
      <div className="flex items-start gap-2">
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-fuchsia-600 grid place-items-center text-white shrink-0">
          <ShoppingBag className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${dark ? "text-white" : "text-neutral-900"}`}>
            Sancta Checkout
          </div>
          <div className={`text-[11px] ${subtleTextCls}`}>
            Secure in-chat order — OTP-gated so only you can approve.
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="sancta-checkout-close"
          className={`h-6 w-6 rounded-full grid place-items-center ${dark ? "text-slate-400 hover:bg-white/10" : "text-neutral-400 hover:bg-neutral-100"}`}
          aria-label="Close checkout"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your cart…
        </div>
      )}
      {error && <div className="text-[12px] text-rose-500">{error}</div>}

      {/* Preview step */}
      {step === "preview" && !loading && totals && (
        <div className="space-y-2" data-testid="sancta-checkout-preview">
          {totals.count === 0 ? (
            <div className={`rounded-lg p-3 text-[12px] ${dark ? "bg-white/5 text-slate-400" : "bg-neutral-50 text-neutral-500"}`}>
              Your cart is empty — add something first, then hit Checkout.
            </div>
          ) : (
            <>
              <div className={`text-[10px] uppercase tracking-widest ${subtleTextCls}`}>
                {totals.count} item{totals.count > 1 ? "s" : ""}
              </div>
              <ul className="space-y-1 max-h-32 overflow-y-auto pr-1">
                {totals.items.map((it: any, i: number) => (
                  <li key={i} className={`flex items-center justify-between gap-2 text-[12px] ${dark ? "text-slate-200" : "text-neutral-700"}`}>
                    <span className="truncate">
                      {it.quantity}× {it.title}
                    </span>
                    <span className="tabular-nums">
                      {money((it.price || 0) * (it.quantity || 1))}
                    </span>
                  </li>
                ))}
              </ul>
              <MoneyRow label="Subtotal" value={money(totals.subtotal)} dark={dark} />
              {totals.vip_discount > 0 && (
                <MoneyRow
                  label={`VIP ${totals.vip_tier || ""}${totals.vip_pct ? ` (${totals.vip_pct}%)` : ""}`}
                  value={`− ${money(totals.vip_discount)}`}
                  dark={dark}
                  accent="emerald"
                />
              )}
              {totals.bundle_discount > 0 && (
                <MoneyRow
                  label={`Bundle discount${totals.bundle_pct ? ` (${totals.bundle_pct}%)` : ""}`}
                  value={`− ${money(totals.bundle_discount)}`}
                  dark={dark}
                  accent="fuchsia"
                />
              )}
              <MoneyRow label="Shipping" value={money(totals.shipping)} dark={dark} />
              <MoneyRow label="GST (18%)" value={money(totals.tax)} dark={dark} />
              <MoneyRow label="Total" value={money(totals.total)} dark={dark} strong />
              <Button
                data-testid="sancta-checkout-next-preview"
                onClick={nextFromPreview}
                disabled={busy}
                className="w-full"
                size="sm"
              >
                Continue to address <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </>
          )}
        </div>
      )}

      {/* Address step */}
      {step === "address" && (
        <div className="space-y-2" data-testid="sancta-checkout-address">
          <div className={`flex items-center gap-1.5 text-[11px] ${subtleTextCls}`}>
            <MapPin className="h-3 w-3" /> Where should we ship this?
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="addr-name"    className={inputCls} placeholder="Full name*"    value={address.name}     onChange={e => patch("name", e.target.value)} />
            <input data-testid="addr-phone"   className={inputCls} placeholder="Phone (E.164)*" value={address.phone}    onChange={e => patch("phone", e.target.value)} />
            <input data-testid="addr-email"   className={`${inputCls} col-span-2`} placeholder="Email*" type="email" value={address.email} onChange={e => patch("email", e.target.value)} />
            <input data-testid="addr-line1"   className={`${inputCls} col-span-2`} placeholder="Address line 1*" value={address.line1} onChange={e => patch("line1", e.target.value)} />
            <input data-testid="addr-line2"   className={`${inputCls} col-span-2`} placeholder="Address line 2 (optional)" value={address.line2 || ""} onChange={e => patch("line2", e.target.value)} />
            <input data-testid="addr-city"    className={inputCls} placeholder="City*"         value={address.city}     onChange={e => patch("city", e.target.value)} />
            <input data-testid="addr-state"   className={inputCls} placeholder="State*"        value={address.state}    onChange={e => patch("state", e.target.value)} />
            <input data-testid="addr-postal"  className={inputCls} placeholder="PIN / ZIP*"    value={address.postal_code} onChange={e => patch("postal_code", e.target.value)} />
            <input data-testid="addr-country" className={inputCls} placeholder="Country*"      value={address.country}  onChange={e => patch("country", e.target.value)} />
          </div>
          <Button
            data-testid="sancta-checkout-submit-address"
            onClick={submitAddress}
            disabled={busy || !isAddressValid(address)}
            className="w-full"
            size="sm"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
            {totals?.otp_unlocked ? "Place order now" : "Send OTP to continue"}
          </Button>
        </div>
      )}

      {/* OTP step */}
      {step === "otp" && (
        <div className="space-y-2" data-testid="sancta-checkout-otp">
          <div className={`flex items-center gap-1.5 text-[11px] ${subtleTextCls}`}>
            <KeyRound className="h-3 w-3" />
            Code sent to <span className={dark ? "text-slate-200" : "text-neutral-800"}>{otpDispatched?.target}</span>. Enter it below.
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="sancta-otp-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
              placeholder="123456"
              inputMode="numeric"
              className={`${inputCls} font-mono tracking-widest text-center`}
            />
            <Button
              data-testid="sancta-otp-verify"
              onClick={verifyOtp}
              disabled={busy || otpCode.length < 4}
              size="sm"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Verify"}
            </Button>
          </div>
          <button
            type="button"
            data-testid="sancta-otp-resend"
            onClick={dispatchOtp}
            disabled={busy}
            className={`text-[11px] ${dark ? "text-fuchsia-300 hover:text-fuchsia-200" : "text-fuchsia-600 hover:text-fuchsia-700"} underline underline-offset-2`}
          >
            Resend code
          </button>
        </div>
      )}

      {/* Placing step */}
      {step === "placing" && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500" data-testid="sancta-checkout-placing">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Placing your order securely…
        </div>
      )}

      {/* Done step */}
      {step === "done" && orderResult?.order && (
        <div className="space-y-2" data-testid="sancta-checkout-done">
          <div className={`rounded-lg p-3 ${dark ? "bg-emerald-500/10 border border-emerald-400/20" : "bg-emerald-50 border border-emerald-200"}`}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <div>
                <div className={`text-sm font-semibold ${dark ? "text-emerald-200" : "text-emerald-800"}`}>
                  Order {orderResult.order.order_number} placed
                </div>
                <div className={`text-[11px] ${dark ? "text-emerald-300/80" : "text-emerald-700"}`}>
                  Total {money(orderResult.order.total)} · Razorpay checkout link ready.
                </div>
              </div>
            </div>
          </div>
          <div className={`rounded-lg p-2 text-[11px] flex items-center gap-1.5 ${dark ? "bg-white/5 text-slate-300" : "bg-neutral-50 text-neutral-600"}`}>
            <Package className="h-3 w-3" />
            Change of mind? Reply "modify order {orderResult.order.order_number}" and I'll edit it — pre-ship only.
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="sancta-checkout-view-order"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                window.location.href = `/orders/${orderResult.order.order_id}`;
              }}
            >
              View order
            </Button>
            <Button
              data-testid="sancta-checkout-continue-chat"
              size="sm"
              className="flex-1"
              onClick={onClose}
            >
              Keep chatting <Sparkles className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const MoneyRow: React.FC<{
  label: string;
  value: string;
  dark: boolean;
  strong?: boolean;
  accent?: "emerald" | "fuchsia";
}> = ({ label, value, dark, strong, accent }) => {
  const accentCls = accent === "emerald"
    ? (dark ? "text-emerald-300" : "text-emerald-600")
    : accent === "fuchsia"
      ? (dark ? "text-fuchsia-300" : "text-fuchsia-600")
      : dark ? "text-slate-200" : "text-neutral-800";
  return (
    <div className={`flex items-center justify-between text-[12px] ${strong ? "pt-1 mt-1 border-t " + (dark ? "border-white/10" : "border-neutral-200") : ""}`}>
      <span className={dark ? "text-slate-400" : "text-neutral-500"}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-semibold" : ""} ${accentCls}`}>
        {value}
      </span>
    </div>
  );
};

export default SanctaCheckoutPanel;
